/**
 * PerformanceDialog — Shared add/edit modal for rodeo performances.
 *
 * One dialog, two contexts:
 *   - RodeoWizard Step 2: caller persists via the zustand store
 *     (updatePerformance / addPerformance).
 *   - DelegateRodeosPage expanded list: caller persists via the
 *     `performances` CRUD hooks.
 *
 * The dialog itself never talks to the API. It owns form state, calls
 * onSubmit(values) with the parsed payload, and the parent decides what
 * to do with it. Callers keep the "pending edit" UX by inspecting the
 * store's _pendingEdit flag — that concern lives outside the dialog.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface PerformanceDialogValues {
  performance_name: string;
  performance_date: string;
  start_time: string;
  /**
   * end_time is intentionally ALWAYS null from the dialog — the product
   * model has a start time per performance, no end time. Kept on the
   * type so existing callers that persist the column don't break, but
   * the dialog never collects it and always emits `null`.
   */
  end_time: string | null;
  is_slack: boolean;
  default_max_contestants: number | null;
}

export interface PerformanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'add' | 'edit';
  initialValues?: Partial<PerformanceDialogValues>;
  onSubmit: (values: PerformanceDialogValues) => void | Promise<void>;
  /**
   * Add-mode only: save the current entry and keep the dialog open with the
   * date advanced by one day. Shown as a third footer button so the user
   * can quickly chain Friday → Saturday → Sunday without reopening the modal.
   */
  onSubmitAndNextDay?: (values: PerformanceDialogValues) => void | Promise<void>;
  /** Optional error to show above the submit button (e.g. API failure). */
  errorMessage?: string | null;
  /** Disables submit while a mutation is in flight. */
  isSaving?: boolean;
  /**
   * Optional slot rendered below the form fields and above the footer. Used
   * by the wizard and My Rodeos list to embed a scoped event list with
   * inline add/edit/delete controls.
   */
  eventsSlot?: ReactNode;
  /**
   * Optional IANA timezone abbreviation hint (e.g. "PT", "MT") rendered next
   * to the Start Time label so the admin remembers which zone the wall-clock
   * value is interpreted in. Omit to hide the hint entirely.
   */
  timezoneAbbreviation?: string;
}

const EMPTY: PerformanceDialogValues = {
  performance_name: '',
  performance_date: '',
  start_time: '',
  end_time: null,
  is_slack: false,
  default_max_contestants: null,
};

export function PerformanceDialog({
  open,
  onOpenChange,
  mode,
  initialValues,
  onSubmit,
  onSubmitAndNextDay,
  errorMessage,
  isSaving,
  eventsSlot,
  timezoneAbbreviation,
}: PerformanceDialogProps) {
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [isSlack, setIsSlack] = useState(false);
  const [maxContestants, setMaxContestants] = useState('');

  // Re-hydrate whenever the dialog opens so a second edit on the same row
  // doesn't pick up stale state from the previous session.
  useEffect(() => {
    if (!open) return;
    const iv = { ...EMPTY, ...(initialValues ?? {}) };
    setName(iv.performance_name ?? '');
    setDate(iv.performance_date ?? '');
    setStartTime(iv.start_time ?? '');
    setIsSlack(!!iv.is_slack);
    setMaxContestants(
      iv.default_max_contestants != null ? String(iv.default_max_contestants) : '',
    );
  }, [open, initialValues]);

  const canSubmit = name.trim().length > 0 && !isSaving;
  // Always show the button in add mode so the feature is discoverable. If the
  // user hasn't entered a date, the "advance by one day" step is a no-op —
  // the dialog still stays open with everything else preserved so they can
  // enter the date + name for the next perf manually.
  const showAddNextDay = mode === 'add' && !!onSubmitAndNextDay;

  const buildValues = (): PerformanceDialogValues => ({
    performance_name: name.trim(),
    performance_date: date,
    start_time: startTime,
    // end_time removed from the UI — always persist as null so the DB
    // column clears on save and old seeded values are overwritten.
    end_time: null,
    is_slack: isSlack,
    default_max_contestants: maxContestants ? parseInt(maxContestants, 10) : null,
  });

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit(buildValues());
  };

  // Parse as local parts to avoid timezone drift — `new Date('YYYY-MM-DD')`
  // is UTC midnight and can roll back a day in western timezones.
  const advanceDateByOneDay = (ymd: string): string => {
    if (!ymd) return ymd;
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) return ymd;
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + 1);
    const yy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  const handleSubmitAndNextDay = async () => {
    if (!canSubmit || !onSubmitAndNextDay) return;
    await onSubmitAndNextDay(buildValues());
    // Keep the dialog open, roll the date forward, leave every other field
    // in place so the user can just tweak the name and hit Add again.
    setDate((prev) => advanceDateByOneDay(prev));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'Edit Performance' : 'Add Performance'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Friday Evening or Slack 1"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Max Contestants</Label>
            <Input
              type="number"
              min={1}
              value={maxContestants}
              onChange={(e) => setMaxContestants(e.target.value)}
              placeholder="Unlimited"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>
              Start Time
              {timezoneAbbreviation && (
                <span className="text-muted-foreground font-normal">
                  {' '}
                  ({timezoneAbbreviation})
                </span>
              )}
            </Label>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3 pt-5 sm:col-span-2">
            <Switch checked={isSlack} onCheckedChange={setIsSlack} />
            <Label>This is a slack run</Label>
          </div>
        </div>

        {eventsSlot && (
          <div className="border-t pt-4 mt-2">
            {eventsSlot}
          </div>
        )}

        {errorMessage && (
          <p className="text-sm text-destructive" role="alert">
            {errorMessage}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          {showAddNextDay && (
            <Button
              variant="secondary"
              onClick={handleSubmitAndNextDay}
              disabled={!canSubmit}
              title="Save this performance and prep the next day's entry"
            >
              Add & Next Day
            </Button>
          )}
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSaving
              ? 'Saving...'
              : mode === 'edit'
                ? 'Save Changes'
                : 'Add Performance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PerformanceDialog;
